import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { fullName, fmtDob, dateTime, time } from '../lib/data';
import { useAuth } from '../lib/auth';
import { useAppointmentExecution } from '../api/hooks';
import { Button } from '../components/ui/button';
import StatusPill from '../components/StatusPill';
import RiskBadge from '../components/RiskBadge';
import SummaryPoints from '../components/SummaryPoints';
import NetworkConfidence from '../components/NetworkConfidence';
import PlanLearnings from '../components/PlanLearnings';
import AgentChain from '../components/AgentChain';
import WorkflowItemCard from '../components/WorkflowItemCard';
import EmptyState from '../components/EmptyState';
import { ArrowLeft, Cpu, FileText, Lock, ExternalLink, ShieldAlert, GitBranch } from 'lucide-react';

export default function AppointmentDetail() {
  const { id } = useParams();
  const { session } = useAuth();
  const practiceId = session?.practice?.id || 'prac_001';
  const { data: view, isLoading } = useAppointmentExecution(id, practiceId);

  if (isLoading || !view) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3"><Link to="/app"><Button variant="ghost" size="icon"><ArrowLeft className="h-4 w-4" /></Button></Link></div>
        <EmptyState icon={Cpu} title="Loading execution…" hint="Assembling the agent chain for this appointment." />
      </div>
    );
  }

  const { patient: pat, provider, payer, check, risk = {}, network, appointment: apt } = view;
  const active = check?.coverage?.active !== false;
  // Open workflow items the agents routed for a human decision on this appointment.
  const openWorkflows = (view.workflows || []).filter((w) => (w.status || 'awaiting_review') === 'awaiting_review');

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/app"><Button variant="ghost" size="icon"><ArrowLeft className="h-4 w-4" /></Button></Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">{fullName(pat)}</h1>
            {check && <StatusPill status={active ? 'active' : 'inactive'} />}
            {network?.status && <StatusPill status={network.status} />}
            <RiskBadge level={risk.level} score={risk.score} showScore />
            {view.visitType && <span className="pill pill-slate">{view.visitType}</span>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{apt?.startTime ? `${dateTime(apt.startTime)} · ` : ''}{apt?.reason || '—'}{provider ? ` · Dr. ${provider.lastName}` : ''}</div>
        </div>
        {check ? (
          <Link to={`/app/eligibility/${check.id}`}><Button variant="outline"><FileText className="h-4 w-4 mr-1.5" />Full 271<ExternalLink className="h-3 w-3 ml-1.5" /></Button></Link>
        ) : (
          <Link to="/app/eligibility/new"><Button className="bg-brand-600 hover:bg-brand-700 text-white"><ShieldAlert className="h-4 w-4 mr-1.5" />Run eligibility</Button></Link>
        )}
      </div>

      {/* Appointment profile */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="dh-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-900">Demographics</div>
            <span className="pill pill-violet"><Lock className="h-3 w-3" />Private</span>
          </div>
          <dl className="space-y-2.5 text-sm">
            {[['Name', fullName(pat)], ['DOB', fmtDob(pat?.dob)], ['Member ID', pat?.memberIdLast4 ? `••••${pat.memberIdLast4}` : '—'], ['Gender', pat?.gender || '—']].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3"><dt className="text-slate-500">{k}</dt><dd className="text-slate-900 text-right">{v}</dd></div>
            ))}
          </dl>
        </div>
        <div className="dh-card p-5">
          <div className="text-sm font-semibold text-slate-900 mb-3">Plan & network</div>
          <dl className="space-y-2.5 text-sm">
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Payer</dt><dd className="text-slate-900 text-right">{payer?.name || '—'}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Plan</dt><dd className="text-slate-900 text-right">{check?.planNameReturned || view.plan?.planName || '—'}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Network</dt><dd>{network?.status ? <StatusPill status={network.status} /> : '—'}</dd></div>
            {network?.confidence && (
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Confidence</dt><dd><NetworkConfidence count={network.confidence.count} threshold={network.confidence.threshold} level={network.confidence.level} /></dd></div>
            )}
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Provider</dt><dd className="text-slate-900 text-right">{provider ? `Dr. ${provider.lastName} · ${provider.npi}` : '—'}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Scheduled</dt><dd className="text-slate-900 text-right">{apt?.startTime ? time(apt.startTime) : '—'}</dd></div>
          </dl>
        </div>
        <div className="ai-surface p-5">
          <div className="text-sm font-semibold text-slate-900 mb-2">Verified summary</div>
          {check ? <SummaryPoints check={check} provider={provider} payer={payer} size="sm" /> : <div className="text-sm text-slate-600">Eligibility has not been run for this appointment yet.</div>}
        </div>
      </div>

      {/* Next actions — the human-in-the-loop workflow items for this appointment */}
      {openWorkflows.length > 0 && (
        <div className="space-y-2.5">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <GitBranch className="h-4 w-4 text-brand-500" />Next actions
            <span className="pill pill-amber tnum">{openWorkflows.length}</span>
          </div>
          <p className="text-xs text-slate-500 -mt-1">Exceptions the agents routed for your decision — handle them right here.</p>
          <div className="grid gap-3 max-w-3xl">
            {openWorkflows.map((w) => <WorkflowItemCard key={w.id} w={w} patient={pat} payer={payer} />)}
          </div>
        </div>
      )}

      {/* Execution stages + agent chain + per-agent responses */}
      <AgentChain view={view} />

      {/* Plan learnings */}
      <PlanLearnings learnings={view.learnings} />
    </div>
  );
}
